import Spinner from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { toast } from "sonner";
import { endpoints } from "../../../hooks/endpoints";
import { getToken, isEmailValid, isPhoneValid } from "../../../hooks/utils";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  AssistantPermissions,
  invokeUserRefetch,
} from "../../../store/slices/storeSlice";
import Separator from "../../Separator";
import CategorySelector from "./CategorySelector";

const EditAssistant = () => {
  const dispatch = useAppDispatch();
  const { userProfileData } = useAppSelector((state) => state.store);

  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState("");
  const [selectedAssistantId, setSelectedAssistantId] = useState<number | null>(
    null
  );

  // Form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");

  // Permissions
  const [permissions, setPermissions] = useState<AssistantPermissions>({
    can_create_quizzes: false,
    can_create_homework: false,
    can_view_quiz_grades: false,
    can_view_homework_grades: false,
    can_renew_subscription: false,
    can_publish_lessons: [],
    can_edit_lessons: [],
    can_view_sales: [],
    can_create_codes: [],
    can_manage_students: [],
  });

  const assistants = userProfileData?.assistants || [];

  const handleSelectAssistant = (assistantId: number) => {
    const assistant = assistants.find((a) => a.id == assistantId);
    if (assistant) {
      setSelectedAssistantId(assistantId);
      setName(assistant.name);
      setEmail(assistant.email);
      setPhone(assistant.phone);
      setPermissions(assistant.permissions);
      setPassword(""); // Reset password field
      setError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!selectedAssistantId) {
      setError("يرجى اختيار مساعد للتعديل");
      return;
    }

    // Validation
    if (!name || name.length < 3) {
      setError("يجب إدخال اسم صحيح (على الأقل 3 أحرف)");
      return;
    }

    const emailValidation = isEmailValid(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.message);
      return;
    }

    const phoneValidation = isPhoneValid(phone);
    if (!phoneValidation.isValid) {
      setError(phoneValidation.message);
      return;
    }

    // Only validate password if it's provided (for reset)
    if (password && password.length < 6) {
      setError("يجب أن تكون كلمة المرور على الأقل 6 أحرف");
      return;
    }

    setIsPending(true);

    try {
      const body: any = {
        assistant_id: selectedAssistantId,
        name,
        email,
        phone,
        permissions,
      };

      // Only include password if it's being reset
      if (password) {
        body.password = password;
      }

      const res = await fetch(endpoints.updateAssistant, {
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
        method: "POST",
        body: JSON.stringify(body),
      });

      const response = await res.json();

      if (!res.ok) {
        throw new Error(response.message || response.data || "حدث خطأ");
      }

      toast.success("تم تحديث بيانات المساعد بنجاح");

      // Reset password field after successful update
      setPassword("");

      dispatch(invokeUserRefetch());
    } catch (err: any) {
      setError(err.message || "حدث خطأ أثناء تحديث بيانات المساعد");
    } finally {
      setIsPending(false);
    }
  };

  return (
    <form
      className="p-3 rounded-lg border shadow-sm bg-white"
      onSubmit={handleSubmit}
    >
      <h4 className="p-2 mb-4 border-b-2 border-primary w-fit font-bold">
        تعديل مدرس مساعد
      </h4>

      {/* Assistant Selection */}
      <div className="mb-4">
        <label htmlFor="select-assistant" className="block font-bold mb-2">
          اختر المدرس المساعد <span className="text-red-500">*</span>
        </label>
        <select
          id="select-assistant"
          className="border border-gray-300 rounded-md p-2 w-full mb-2 focus:outline-none focus:ring-2 focus:ring-primary"
          value={selectedAssistantId || ""}
          onChange={(e) => handleSelectAssistant(Number(e.target.value))}
        >
          <option value="">اختر مدرس مساعد...</option>
          {assistants.map((assistant) => (
            <option key={assistant.id} value={assistant.id}>
              {assistant.name} - {assistant.phone}
            </option>
          ))}
        </select>
      </div>

      {selectedAssistantId && (
        <>
          <Separator />

          {/* Basic Information */}
          <div className="mb-4">
            <label htmlFor="assistant-name" className="block font-bold mb-2">
              الاسم <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="assistant-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="أدخل اسم المدرس المساعد"
              className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="assistant-email" className="block font-bold mb-2">
              البريد الإلكتروني <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="assistant-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@domain.com"
              className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="assistant-phone" className="block font-bold mb-2">
              رقم الهاتف <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="assistant-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="01xxxxxxxxx"
              className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-2 focus:ring-primary text-end"
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="assistant-password"
              className="block font-bold mb-2"
            >
              تغيير كلمة المرور (اتركه فارغاً إذا كنت لا تريد التغيير)
            </label>
            <input
              type="password"
              id="assistant-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="أدخل كلمة المرور الجديدة (على الأقل 6 أحرف)"
              className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <Separator />

          {/* Permissions */}
          <h5 className="font-bold mb-3 text-lg">الصلاحيات</h5>

          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={permissions.can_create_quizzes}
                onChange={(e) =>
                  setPermissions({
                    ...permissions,
                    can_create_quizzes: e.target.checked,
                  })
                }
                className="w-4 h-4"
              />
              <span className="font-medium">يمكنه إنشاء امتحانات</span>
            </label>
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={permissions.can_create_homework}
                onChange={(e) =>
                  setPermissions({
                    ...permissions,
                    can_create_homework: e.target.checked,
                  })
                }
                className="w-4 h-4"
              />
              <span className="font-medium">يمكنه إنشاء واجبات</span>
            </label>
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={permissions.can_view_quiz_grades}
                onChange={(e) =>
                  setPermissions({
                    ...permissions,
                    can_view_quiz_grades: e.target.checked,
                  })
                }
                className="w-4 h-4"
              />
              <span className="font-medium">يمكنه عرض درجات الامتحانات</span>
            </label>
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={permissions.can_view_homework_grades}
                onChange={(e) =>
                  setPermissions({
                    ...permissions,
                    can_view_homework_grades: e.target.checked,
                  })
                }
                className="w-4 h-4"
              />
              <span className="font-medium">يمكنه عرض درجات الواجبات</span>
            </label>
          </div>
          
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={permissions.can_renew_subscription}
                onChange={(e) =>
                  setPermissions({
                    ...permissions,
                    can_renew_subscription: e.target.checked,
                  })
                }
                className="w-4 h-4"
              />
              <span className="font-medium">يمكنه تجديد إشتراك المنصة</span>
            </label>
          </div>

          <Separator />

          <CategorySelector
            title="يمكنه نشر حصص في الأقسام التالية"
            selectedCategories={permissions.can_publish_lessons}
            onChange={(cats) =>
              setPermissions({ ...permissions, can_publish_lessons: cats })
            }
          />

          <CategorySelector
            title="يمكنه تعديل حصص في الأقسام التالية"
            selectedCategories={permissions.can_edit_lessons}
            onChange={(cats) =>
              setPermissions({ ...permissions, can_edit_lessons: cats })
            }
          />

          <CategorySelector
            title="يمكنه عرض مبيعات الأقسام التالية"
            selectedCategories={permissions.can_view_sales}
            onChange={(cats) =>
              setPermissions({ ...permissions, can_view_sales: cats })
            }
          />

          <CategorySelector
            title="يمكنه إنشاء أكواد للأقسام التالية"
            selectedCategories={permissions.can_create_codes}
            onChange={(cats) =>
              setPermissions({ ...permissions, can_create_codes: cats })
            }
          />

          <CategorySelector
            title="يمكنه إدارة الطلاب الذين اشتروا من الأقسام التالية"
            selectedCategories={permissions.can_manage_students}
            onChange={(cats) =>
              setPermissions({ ...permissions, can_manage_students: cats })
            }
          />

          <Separator />

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={isPending}>
              {isPending ? <Spinner variant="button" /> : "تحديث البيانات"}
            </Button>
          </div>
        </>
      )}

      {!!error && (
        <div
          className="bg-red-100 text-red-700 rounded-lg px-4 py-2 mt-3 w-fit"
          role="alert"
        >
          {error}
        </div>
      )}
    </form>
  );
};

export default EditAssistant;
